import React, { useState, useEffect } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BellIcon,
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';

const FollowUpDashboard = ({ onNotificationsUpdate, onFollowUpsUpdate }) => {
  const [followUps, setFollowUps] = useState([]);
  const [todayFollowUps, setTodayFollowUps] = useState([]);
  const [missedFollowUps, setMissedFollowUps] = useState([]);
  const [upcomingFollowUps, setUpcomingFollowUps] = useState([]);
  const [completedFollowUps, setCompletedFollowUps] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // all, missed, today, upcoming, completed

  useEffect(() => {
    loadFollowUps();
    const interval = setInterval(loadFollowUps, 60000); // Refresh every minute

    const handleGlobalRefresh = () => {
      console.log('🔄 Global follow-up refresh triggered');
      loadFollowUps();
    };

    window.addEventListener('followUpCreated', handleGlobalRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('followUpCreated', handleGlobalRefresh);
    };
  }, []);

  const loadFollowUps = async () => {
    try {
      console.log('🔄 Loading follow-ups from database...');

      // Get current user ID from token
      const token = localStorage.getItem('authToken');
      let currentUserId = null;

      if (token) {
        try {
          const { jwtDecode } = await import('jwt-decode');
          const decoded = jwtDecode(token);
          currentUserId = decoded.id || decoded._id;
          console.log('👤 Current user ID:', currentUserId);
        } catch (error) {
          console.error('Error decoding token:', error);
        }
      }

      // Import services dynamically
      const FollowUpService = (await import('../services/followUpService')).default;
      const LeadsService = (await import('../services/leadsService')).default;


      // Load from database - pass user ID to get only user's follow-ups
      const upcomingResult = await FollowUpService.getUpcomingFollowUps(currentUserId, 2); // Next 2 days
      const overdueResult = await FollowUpService.getOverdueFollowUps(currentUserId);


      let allFollowUps = [];

      if (upcomingResult.success && upcomingResult.data) {
        console.log('✅ Loaded upcoming follow-ups:', upcomingResult.data.length);
        allFollowUps = [...allFollowUps, ...upcomingResult.data];
      }

      if (overdueResult.success && overdueResult.data) {
        console.log('✅ Loaded overdue follow-ups:', overdueResult.data.length);
        allFollowUps = [...allFollowUps, ...overdueResult.data];
      }

      // Also load from leads' followUps arrays (both pending and completed)
      // Backend already filters leads by company/employee, so we just need to extract follow-ups
      const leadsResult = await LeadsService.getAllLeads();
      if (leadsResult.success && leadsResult.data) {
        console.log('📊 Processing follow-ups from', leadsResult.data.length, 'leads');
        leadsResult.data.forEach(lead => {
          if (lead.followUps && lead.followUps.length > 0) {
            lead.followUps.forEach(followUp => {
              allFollowUps.push({
                ...followUp,
                leadId: lead._id || lead.id,
                leadName: lead.name,
                leadPhone: lead.phone,
                leadEmail: lead.email,
                leadCompany: lead.company,
                scheduledDate: followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date,
                status: followUp.status || (followUp.completed ? 'completed' : 'pending'),
                type: followUp.type || 'call',
                priority: followUp.priority || 'medium'
              });
            });
          }
        });
      }

      // Remove duplicates
      const uniqueFollowUps = allFollowUps.reduce((acc, current) => {
        const exists = acc.find(item =>
          (item._id && item._id === current._id) ||
          (item.leadId === current.leadId && item.scheduledDate === current.scheduledDate)
        );
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      console.log('📊 Total unique follow-ups:', uniqueFollowUps.length);

      setFollowUps(uniqueFollowUps);
      categorizeFollowUps(uniqueFollowUps);
      generateNotifications(uniqueFollowUps);

      // Update parent component with follow-ups
      if (onFollowUpsUpdate) {
        onFollowUpsUpdate(uniqueFollowUps);
      }
    } catch (error) {
      console.error('❌ Error loading follow-ups:', error);
    }
  };

  const categorizeFollowUps = (followUps) => {
    console.log('📊 Categorizing follow-ups:', followUps);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    // Filter pending follow-ups (not completed)
    const pending = followUps.filter(f => {
      const isCompleted = f.status === 'completed' || f.completed === true;
      console.log('Follow-up:', f.leadName, 'Status:', f.status, 'Completed:', f.completed, 'Is Pending:', !isCompleted);
      return !isCompleted;
    });

    console.log('📋 Pending follow-ups:', pending.length);

    // Today's follow-ups
    const todayItems = pending.filter(f => {
      if (!f.scheduledDate) return false;
      const followUpDate = new Date(f.scheduledDate);
      if (isNaN(followUpDate.getTime())) return false; // Invalid date check

      const isToday = followUpDate >= today && followUpDate < tomorrow;
      if (isToday) console.log('✅ Today:', f.leadName, followUpDate);
      return isToday;
    });

    // Missed follow-ups (overdue)
    const missedItems = pending.filter(f => {
      if (!f.scheduledDate) return false;
      const followUpDate = new Date(f.scheduledDate);
      if (isNaN(followUpDate.getTime())) return false; // Invalid date check

      const isMissed = followUpDate < today;
      if (isMissed) console.log('🔴 Missed:', f.leadName, followUpDate);
      return isMissed;
    });

    // Upcoming follow-ups (next 2 days after tomorrow)
    const upcomingItems = pending.filter(f => {
      if (!f.scheduledDate) return false;
      const followUpDate = new Date(f.scheduledDate);
      if (isNaN(followUpDate.getTime())) return false; // Invalid date check

      const twoDaysFromNow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
      const isUpcoming = followUpDate >= tomorrow && followUpDate <= twoDaysFromNow;
      if (isUpcoming) console.log('📅 Upcoming:', f.leadName, followUpDate);
      return isUpcoming;
    });

    // Completed follow-ups (last 7 days)
    const completedItems = followUps.filter(f => {
      const isCompleted = f.status === 'completed' || f.completed === true;
      if (isCompleted) console.log('✅ Completed:', f.leadName, f.completedAt);
      return isCompleted;
    });

    console.log('📊 Categorized:', {
      today: todayItems.length,
      missed: missedItems.length,
      upcoming: upcomingItems.length,
      completed: completedItems.length
    });

    setTodayFollowUps(todayItems);
    setMissedFollowUps(missedItems);
    setUpcomingFollowUps(upcomingItems);
    setCompletedFollowUps(completedItems);
  };

  const generateNotifications = (followUps) => {
    const now = new Date();
    const newNotifications = [];

    console.log('Generating notifications for', followUps.length, 'follow-ups');

    followUps.forEach(followUp => {
      if (followUp.status === 'pending') {
        const followUpTime = new Date(followUp.scheduledDate);
        const timeDiff = followUpTime.getTime() - now.getTime();
        const hoursUntil = timeDiff / (1000 * 60 * 60);

        console.log(`Follow-up ${followUp.id}: ${followUp.leadName}, Hours until: ${hoursUntil}`);

        // Generate unique notifications based on time remaining
        if (hoursUntil <= 0) {
          // Overdue
          newNotifications.push({
            id: `overdue-${followUp.id}`,
            type: 'overdue',
            title: 'Overdue Follow-up',
            message: `Follow-up with ${followUp.leadName} is overdue`,
            followUp,
            priority: 'urgent',
            createdAt: now.toISOString()
          });
        } else if (hoursUntil <= 2) {
          // Due within 2 hours
          newNotifications.push({
            id: `due-soon-${followUp.id}`,
            type: 'due_soon',
            title: 'Follow-up Due Soon',
            message: `Follow-up with ${followUp.leadName} due in ${Math.round(hoursUntil)} hours`,
            followUp,
            priority: 'high',
            createdAt: now.toISOString()
          });
        } else if (hoursUntil <= 24) {
          // Due within 24 hours (today)
          newNotifications.push({
            id: `due-today-${followUp.id}`,
            type: 'due_today',
            title: 'Follow-up Due Today',
            message: `Follow-up with ${followUp.leadName} scheduled for today`,
            followUp,
            priority: 'medium',
            createdAt: now.toISOString()
          });
        } else if (hoursUntil <= 168) {
          // Due within 7 days (upcoming)
          newNotifications.push({
            id: `upcoming-${followUp.id}`,
            type: 'upcoming',
            title: 'Upcoming Follow-up',
            message: `Follow-up with ${followUp.leadName} scheduled for ${new Date(followUp.scheduledDate).toLocaleDateString()}`,
            followUp,
            priority: 'low',
            createdAt: now.toISOString()
          });
        }
      }
    });

    console.log('Generated notifications:', newNotifications);
    setNotifications(newNotifications);

    // Update parent component with notifications
    if (onNotificationsUpdate) {
      onNotificationsUpdate(newNotifications);
    }
  };

  const markFollowUpCompleted = async (followUp) => {
    try {
      console.log('✅ Marking follow-up as completed:', followUp);

      // Import services
      const LeadsService = (await import('../services/leadsService')).default;

      // Get the lead
      const leadResult = await LeadsService.getLeadById(followUp.leadId);
      if (!leadResult.success) {
        console.error('Failed to load lead');
        alert('Failed to update follow-up');
        return;
      }

      const lead = leadResult.data;

      // Update the follow-up in lead's followUps array
      const updatedFollowUps = (lead.followUps || []).map(f => {
        // Match by date and type since we might not have _id
        if (f.nextFollowUpDate === followUp.scheduledDate ||
          f.scheduledDate === followUp.scheduledDate ||
          f.date === followUp.scheduledDate) {
          return {
            ...f,
            completed: true,
            completedAt: new Date().toISOString(),
            status: 'completed'
          };
        }
        return f;
      });

      // Update lead in database
      const updatePayload = {
        title: lead.customFields?.title || lead.title,
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        company: lead.company,
        source: lead.source,
        budget: lead.budget,
        status: lead.status,
        priority: lead.priority || 'medium',
        value: lead.value || 0,
        notes: lead.notes || '',
        followUps: updatedFollowUps
      };

      const result = await LeadsService.updateLead(lead._id, updatePayload);

      if (result.success) {
        console.log('✅ Follow-up marked as completed successfully');
        alert('Follow-up marked as completed!');
        loadFollowUps(); // Refresh the data
      } else {
        console.error('Failed to update lead');
        alert('Failed to update follow-up');
      }
    } catch (error) {
      console.error('❌ Error updating follow-up:', error);
      alert('Failed to update follow-up');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Time';

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const FollowUpCard = ({ followUp, type }) => (
    <div className={`p-4 rounded-lg border-l-4 ${getPriorityColor(followUp.priority)} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <UserIcon className="h-4 w-4 text-gray-500 mr-2" />
            <h4 className="font-semibold text-gray-900">{followUp.leadName}</h4>
            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${followUp.priority === 'urgent' ? 'bg-red-100 text-red-800' :
              followUp.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                followUp.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
              }`}>
              {followUp.priority?.toUpperCase()}
            </span>
          </div>

          {followUp.leadCompany && (
            <div className="flex items-center mb-2 text-sm text-gray-600">
              <BuildingOfficeIcon className="h-4 w-4 mr-2" />
              {followUp.leadCompany}
            </div>
          )}

          <div className="flex items-center mb-2 text-sm text-gray-600">
            <ClockIcon className="h-4 w-4 mr-2" />
            {formatDate(followUp.scheduledDate)} at {formatTime(followUp.scheduledDate)}
          </div>

          {followUp.leadPhone && (
            <div className="flex items-center mb-2 text-sm text-gray-600">
              <PhoneIcon className="h-4 w-4 mr-2" />
              {followUp.leadPhone}
            </div>
          )}

          {followUp.notes && (
            <p className="text-sm text-gray-700 mb-3">{followUp.notes}</p>
          )}

          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 text-xs rounded-full ${followUp.type === 'call' ? 'bg-blue-100 text-blue-800' :
              followUp.type === 'email' ? 'bg-purple-100 text-purple-800' :
                followUp.type === 'meeting' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
              }`}>
              {followUp.type === 'call' && <PhoneIcon className="h-3 w-3 inline mr-1" />}
              {followUp.type === 'email' && <EnvelopeIcon className="h-3 w-3 inline mr-1" />}
              {followUp.type?.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="flex flex-col space-y-2 ml-4">
          <button
            onClick={() => markFollowUpCompleted(followUp)}
            className="flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
            title="Mark as completed"
          >
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            Mark Complete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div
          onClick={() => setActiveFilter(activeFilter === 'missed' ? 'all' : 'missed')}
          className={`rounded-lg p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${activeFilter === 'missed'
            ? 'bg-red-100 border-2 border-red-500 shadow-lg'
            : 'bg-red-50 border border-red-200'
            }`}
        >
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">Missed Follow-ups</h3>
              <p className="text-2xl font-bold text-red-600">{missedFollowUps.length}</p>
            </div>
          </div>
          {activeFilter === 'missed' && (
            <div className="mt-2 text-xs text-red-700 font-medium">
              ✓ Filtered
            </div>
          )}
        </div>

        <div
          onClick={() => setActiveFilter(activeFilter === 'today' ? 'all' : 'today')}
          className={`rounded-lg p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${activeFilter === 'today'
            ? 'bg-blue-100 border-2 border-blue-500 shadow-lg'
            : 'bg-blue-50 border border-blue-200'
            }`}
        >
          <div className="flex items-center">
            <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-blue-900">Today's Follow-ups</h3>
              <p className="text-2xl font-bold text-blue-600">{todayFollowUps.length}</p>
            </div>
          </div>
          {activeFilter === 'today' && (
            <div className="mt-2 text-xs text-blue-700 font-medium">
              ✓ Filtered
            </div>
          )}
        </div>

        <div
          onClick={() => setActiveFilter(activeFilter === 'upcoming' ? 'all' : 'upcoming')}
          className={`rounded-lg p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${activeFilter === 'upcoming'
            ? 'bg-green-100 border-2 border-green-500 shadow-lg'
            : 'bg-green-50 border border-green-200'
            }`}
        >
          <div className="flex items-center">
            <ArrowRightIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-green-900">Upcoming</h3>
              <p className="text-2xl font-bold text-green-600">{upcomingFollowUps.length}</p>
            </div>
          </div>
          {activeFilter === 'upcoming' && (
            <div className="mt-2 text-xs text-green-700 font-medium">
              ✓ Filtered
            </div>
          )}
        </div>

        <div
          onClick={() => setActiveFilter(activeFilter === 'completed' ? 'all' : 'completed')}
          className={`rounded-lg p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${activeFilter === 'completed'
            ? 'bg-purple-100 border-2 border-purple-500 shadow-lg'
            : 'bg-purple-50 border border-purple-200'
            }`}
        >
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-purple-900">Completed</h3>
              <p className="text-2xl font-bold text-purple-600">{completedFollowUps.length}</p>
            </div>
          </div>
          {activeFilter === 'completed' && (
            <div className="mt-2 text-xs text-purple-700 font-medium">
              ✓ Filtered
            </div>
          )}
        </div>
      </div>

      {/* Filter Info */}
      {activeFilter !== 'all' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-blue-800 font-medium">
              Showing: {
                activeFilter === 'missed' ? 'Missed Follow-ups' :
                  activeFilter === 'today' ? "Today's Follow-ups" :
                    activeFilter === 'upcoming' ? 'Upcoming Follow-ups' :
                      'Completed Follow-ups'
              }
            </span>
          </div>
          <button
            onClick={() => setActiveFilter('all')}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Clear Filter
          </button>
        </div>
      )}

      {/* Follow-up Sections */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Lead Info</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Follow-up Type</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Schedule</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(() => {
                const displayItems = activeFilter === 'all' ? followUps :
                                    activeFilter === 'missed' ? missedFollowUps :
                                    activeFilter === 'today' ? todayFollowUps :
                                    activeFilter === 'upcoming' ? upcomingFollowUps :
                                    completedFollowUps;
                
                if (displayItems.length === 0) {
                  return (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="text-4xl mb-4">📋</div>
                        <h3 className="text-lg font-bold text-gray-700 mb-1">No Follow-ups Found</h3>
                        <p className="text-gray-500 text-sm">
                          {activeFilter === 'all' ? "You're all caught up!" : `No ${activeFilter} follow-ups at the moment.`}
                        </p>
                      </td>
                    </tr>
                  );
                }

                return displayItems.map((followUp, index) => (
                  <tr key={followUp.id || index} className="hover:bg-purple-50/30 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900">{followUp.leadName}</span>
                        {followUp.leadCompany && !/^[0-9a-fA-F]{24}$/.test(followUp.leadCompany) && (
                          <span className="text-xs text-gray-500">{followUp.leadCompany}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        followUp.type === 'call' ? 'bg-blue-100 text-blue-800' :
                        followUp.type === 'email' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {followUp.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-800">
                          📅 {formatDate(followUp.scheduledDate || followUp.completedAt || followUp.date)}
                        </span>
                        <span className="text-xs text-gray-500">
                          🕐 {formatTime(followUp.scheduledDate || followUp.completedAt || followUp.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-bold border-2 ${
                        followUp.priority === 'urgent' ? 'border-red-500 bg-red-50 text-red-700' :
                        followUp.priority === 'high' ? 'border-orange-500 bg-orange-50 text-orange-700' :
                        followUp.priority === 'medium' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                        'border-green-500 bg-green-50 text-green-700'
                      }`}>
                        {followUp.priority?.toUpperCase() || 'MEDIUM'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${
                        followUp.status === 'completed' || followUp.completed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {followUp.status === 'completed' || followUp.completed ? '✅ Completed' : '⏳ Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {!(followUp.status === 'completed' || followUp.completed) && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => markFollowUpCompleted(followUp)}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-600 hover:text-white transition-all duration-200 text-sm font-bold border-2 border-emerald-100 shadow-sm"
                          >
                            <span>✅</span>
                            <span>Complete</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FollowUpDashboard;
